import React, { useEffect, useState } from "react";
import { City, Company, TrackIds } from "../../types/types";
import { useTranslation } from "react-i18next";
import {
    addCityFromCompany,
    addTrackToCompany,
    getCitiesAdmin,
    getTrackIds,
    removeCityFromCompany,
    removeTrackFromCompany,
    updateCompanyAudioLimit,
    updateCompanyTextLimit,
    updateCompanyAddons,
} from "../../api/administration";
import { getHoramaConfigStatus, updateHoramaConfig, removeHoramaConfig, HoramaConfigStatus } from "../../api/horama";
import { useLanguage } from "../../context/useLanguage";
import NumberInput from "../common/NumberInput";
import TextInput from "../ui/TextInput";
import HorizontalSeparator from "../common/HorizontalSeparator";
import { COMPANY_ADDONS } from "../../utils/addons";
import styles from "./CompanyEditModal.module.css";
import { ModalPortal } from "./ModalPortal";

const HIDDEN_ADDONS = [COMPANY_ADDONS.LIVETRANSLATION, COMPANY_ADDONS.CHATBOT] as const;

type Addon = (typeof COMPANY_ADDONS)[keyof typeof COMPANY_ADDONS];
const HIDDEN_SET = new Set<Addon>(HIDDEN_ADDONS);

const VISIBLE_ADDONS = (Object.values(COMPANY_ADDONS) as Addon[]).filter((a) => !HIDDEN_SET.has(a));

interface CompanyEditModalProps {
    company: Company;
    isOpen: boolean;
    onClose: () => void;
}

const CompanyEditModal: React.FC<CompanyEditModalProps> = ({ company, isOpen, onClose }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const [activeTab, setActiveTab] = useState<"tracks" | "cities" | "info" | "addons" | "horama">("info");
    const [, setLoading] = useState<boolean>(true);
    const { language } = useLanguage();
    const [cities, setCities] = useState<City[]>([]);
    const [tracks, setTracks] = useState<TrackIds[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<Company>(company);

    // Horama config state
    const [horamaConfig, setHoramaConfig] = useState<HoramaConfigStatus | null>(null);
    const [horamaInstanceUrl, setHoramaInstanceUrl] = useState<string>("");
    const [horamaApiKey, setHoramaApiKey] = useState<string>("");
    const [horamaLoading, setHoramaLoading] = useState<boolean>(false);
    const [horamaError, setHoramaError] = useState<string>("");
    const [horamaSuccess, setHoramaSuccess] = useState<string>("");

    useEffect(() => {
        if (isOpen) {
            setSelectedCompany(company); // reset à chaque ouverture
            fetchCities();
            fetchTrackIds();
            fetchHoramaConfig();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, company, language]);

    const fetchHoramaConfig = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) return;

            const response = await getHoramaConfigStatus({ token, companyId: company.id });
            setHoramaConfig(response);
            if (response?.instanceUrl) {
                setHoramaInstanceUrl(response.instanceUrl);
            }
        } catch (error) {
            console.error("Failed to fetch Horama config:", error);
        }
    };

    const handleSaveHoramaConfig = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            setHoramaError(t("tokenNotFound"));
            return;
        }

        if (!horamaInstanceUrl || !horamaApiKey) {
            setHoramaError(t("horamaFieldsRequired"));
            return;
        }

        // Validate URL format
        if (!horamaInstanceUrl.startsWith("https://")) {
            setHoramaError(t("horamaUrlMustBeHttps"));
            return;
        }

        // Validate API key format
        if (!horamaApiKey.startsWith("prism_")) {
            setHoramaError(t("horamaApiKeyFormat"));
            return;
        }

        setHoramaLoading(true);
        setHoramaError("");
        setHoramaSuccess("");

        try {
            const response = await updateHoramaConfig({
                token,
                companyId: selectedCompany.id,
                instanceUrl: horamaInstanceUrl,
                apiKey: horamaApiKey,
                testConnection: true,
            });

            setHoramaConfig(response.horamaConfig);
            setHoramaSuccess(t("horamaConfigSaved"));
            setHoramaApiKey(""); // Clear API key after successful save
        } catch (error: any) {
            console.error("Failed to save Horama config:", error);
            setHoramaError(error?.message || t("horamaConfigError"));
        } finally {
            setHoramaLoading(false);
        }
    };

    const handleRemoveHoramaConfig = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            setHoramaError(t("tokenNotFound"));
            return;
        }

        if (!window.confirm(t("confirmRemoveHoramaConfig"))) {
            return;
        }

        setHoramaLoading(true);
        setHoramaError("");
        setHoramaSuccess("");

        try {
            await removeHoramaConfig({ token, companyId: selectedCompany.id });
            setHoramaConfig(null);
            setHoramaInstanceUrl("");
            setHoramaApiKey("");
            setHoramaSuccess(t("horamaConfigRemoved"));
        } catch (error: any) {
            console.error("Failed to remove Horama config:", error);
            setHoramaError(error?.message || t("horamaRemoveError"));
        } finally {
            setHoramaLoading(false);
        }
    };

    const fetchCities = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getCitiesAdmin(token, language);
            setCities(response);
        } catch (error) {
            console.error("Failed to fetch cities", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTrackIds = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                console.error("Token not found");
                return;
            }
            const response = await getTrackIds(token);
            setTracks(response);
        } catch (error) {
            console.error("Failed to fetch cities", error);
        } finally {
            setLoading(false);
        }
    };

    const isCityAssociated = (cityId: string) => {
        return selectedCompany.cities?.some((cityObj) => cityObj.id === cityId);
    };

    const isTrackAssociated = (trackId: string) => {
        return selectedCompany.tracks?.some((trackObj) => trackObj.id === trackId);
    };

    const handleAddCity = (cityId: string) => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Token not found");
            return;
        }
        addCityFromCompany({
            cityId,
            companyId: selectedCompany.id,
            token: token,
        });
        if (!isCityAssociated(cityId)) {
            setSelectedCompany((prev) => ({
                ...prev,
                cities: [...prev.cities, { id: cityId }],
            }));
        }
    };

    const handleAddTrack = (trackId: string) => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Token not found");
            return;
        }
        addTrackToCompany({
            trackId,
            companyId: selectedCompany.id,
            token: token,
        });
        if (!isCityAssociated(trackId)) {
            setSelectedCompany((prev) => ({
                ...prev,
                tracks: [...prev.tracks, { id: trackId }],
            }));
        }
    };

    const handleRemoveCity = (cityId: string) => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Token not found");
            return;
        }
        removeCityFromCompany({
            cityId,
            companyId: selectedCompany.id,
            token: token,
        });
        setSelectedCompany((prev) => ({
            ...prev,
            cities: prev.cities.filter((cityObj) => cityObj.id !== cityId),
        }));
    };

    const handleRemoveTrack = (trackId: string) => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Token not found");
            return;
        }
        removeTrackFromCompany({
            trackId,
            companyId: selectedCompany.id,
            token: token,
        });
        setSelectedCompany((prev) => ({
            ...prev,
            tracks: prev.tracks.filter((trackObj) => trackObj.id !== trackId),
        }));
    };

    const updateLimitCharTextGen = async (limit: number) => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Token not found");
            return;
        }
        await updateCompanyTextLimit({ token, companyId: selectedCompany.id, limit });
    };

    const updateLimitCharAudioGen = async (limit: number) => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Token not found");
            return;
        }
        await updateCompanyAudioLimit({ token, companyId: selectedCompany.id, limit });
    };

    const updateCompanyAddonsHandler = async (addons: string[]) => {
        const token = localStorage.getItem("token");
        if (!token) {
            console.error("Token not found");
            return;
        }
        await updateCompanyAddons({ token, companyId: selectedCompany.id, addons });
    };

    if (!isOpen) return null;

    console.log("Tracks:", tracks);

    return (
        <ModalPortal>
            <div className={styles.overlay}>
                <div className={styles.content}>
                    <h2>
                        {t("editCompany")} - {selectedCompany.name}
                    </h2>

                    <div className={styles.tabs}>
                        <button
                            className={`${styles.tab}${activeTab === "tracks" ? ` ${styles.tabActive}` : ""}`}
                            onClick={() => setActiveTab("tracks")}
                        >
                            {t("tracks")}
                        </button>
                        <button
                            className={`${styles.tab}${activeTab === "cities" ? ` ${styles.tabActive}` : ""}`}
                            onClick={() => setActiveTab("cities")}
                        >
                            {t("cities")}
                        </button>
                        <button
                            className={`${styles.tab}${activeTab === "info" ? ` ${styles.tabActive}` : ""}`}
                            onClick={() => setActiveTab("info")}
                        >
                            {t("info")}
                        </button>
                        <button
                            className={`${styles.tab}${activeTab === "addons" ? ` ${styles.tabActive}` : ""}`}
                            onClick={() => setActiveTab("addons")}
                        >
                            {t("addons")}
                        </button>
                        <button
                            className={`${styles.tab}${activeTab === "horama" ? ` ${styles.tabActive}` : ""}`}
                            onClick={() => setActiveTab("horama")}
                        >
                            Horama
                        </button>
                    </div>

                    <HorizontalSeparator />

                    <div className={styles.tabContent}>
                        {activeTab === "tracks" && (
                            <div className={styles.citiesManagement}>
                                <div className={styles.citiesColumns}>
                                    {/* Villes associées */}
                                    <div className={styles.citiesColumn}>
                                        <h3>{t("associatedTracks")}</h3>
                                        <ul>
                                            {tracks
                                                .sort((a, b) =>
                                                    a?.name && b?.name ? a.name.localeCompare(b?.name) : 0,
                                                )
                                                .filter((track) => isTrackAssociated(track.id))
                                                .map((track) => (
                                                    <li key={track.id}>
                                                        {track.name}
                                                        <button
                                                            className={styles.removeBtn}
                                                            onClick={() => handleRemoveTrack(track.id)}
                                                        >
                                                            {t("unlink")}
                                                        </button>
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>

                                    {/* Villes non associées */}
                                    <div className={styles.citiesColumn}>
                                        <h3>{t("availableTracks")}</h3>
                                        <ul>
                                            {tracks
                                                .sort((a, b) =>
                                                    a?.name && b?.name ? a.name.localeCompare(b?.name) : 0,
                                                )
                                                .filter((track) => !isTrackAssociated(track.id))
                                                .map((track) => (
                                                    <li key={track.id}>
                                                        {track.name}
                                                        <button
                                                            className={styles.addBtn}
                                                            onClick={() => handleAddTrack(track.id)}
                                                        >
                                                            {t("link")}
                                                        </button>
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "cities" && (
                            <div className={styles.citiesManagement}>
                                <div className={styles.citiesColumns}>
                                    {/* Villes associées */}
                                    <div className={styles.citiesColumn}>
                                        <h3>{t("associatedCities")}</h3>
                                        <ul>
                                            {cities
                                                .sort((a, b) =>
                                                    a?.officialName && b?.officialName
                                                        ? a.officialName.localeCompare(b?.officialName)
                                                        : 0,
                                                )
                                                .filter((city) => isCityAssociated(city.id))
                                                .map((city) => (
                                                    <li key={city.id}>
                                                        {city.officialName}
                                                        <button
                                                            className={styles.removeBtn}
                                                            onClick={() => handleRemoveCity(city.id)}
                                                        >
                                                            {t("unlink")}
                                                        </button>
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>

                                    {/* Villes non associées */}
                                    <div className={styles.citiesColumn}>
                                        <h3>{t("availableCities")}</h3>
                                        <ul>
                                            {cities
                                                .filter((city) => !isCityAssociated(city.id))
                                                .map((city) => (
                                                    <li key={city.id}>
                                                        {city.officialName}
                                                        <button
                                                            className={styles.addBtn}
                                                            onClick={() => handleAddCity(city.id)}
                                                        >
                                                            {t("link")}
                                                        </button>
                                                    </li>
                                                ))}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === "info" && (
                            <div>
                                <p>
                                    {t("usedCharTextGen")} {selectedCompany.usedCharTextGen}
                                </p>
                                <p>
                                    {t("usedCharAudioGen")} {selectedCompany.usedCharAudioGen}
                                </p>
                                <h4>{t("limitCharTextGen")}</h4>
                                <NumberInput
                                    placeholder={t("selectNumber")}
                                    value={selectedCompany.limitCharTextGen}
                                    size="medium"
                                    onChange={(e) => {
                                        setSelectedCompany({
                                            ...selectedCompany,
                                            limitCharTextGen: parseInt(e.target.value),
                                        });
                                        updateLimitCharTextGen(parseInt(e.target.value));
                                    }}
                                />

                                <h4>{t("limitCharAudioGen")}</h4>
                                <NumberInput
                                    placeholder={t("selectNumber")}
                                    value={selectedCompany.limitCharAudioGen}
                                    size="medium"
                                    onChange={(e) => {
                                        setSelectedCompany({
                                            ...selectedCompany,
                                            limitCharAudioGen: parseInt(e.target.value),
                                        });
                                        updateLimitCharAudioGen(parseInt(e.target.value));
                                    }}
                                />
                            </div>
                        )}

                        {activeTab === "addons" && (
                            <div className={styles.addonsManagement}>
                                <h3>{t("companyAddons")}</h3>
                                <div className={styles.addonsList}>
                                    {VISIBLE_ADDONS.map((addon) => (
                                        <div key={addon} className={styles.addonItem}>
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedCompany.addons?.includes(addon) || false}
                                                    onChange={(e) => {
                                                        const isChecked = e.target.checked;
                                                        const currentAddons = selectedCompany.addons || [];
                                                        let newAddons: string[];

                                                        if (isChecked) {
                                                            newAddons = [...currentAddons, addon];
                                                        } else {
                                                            newAddons = currentAddons.filter((a) => a !== addon);
                                                        }

                                                        setSelectedCompany({
                                                            ...selectedCompany,
                                                            addons: newAddons,
                                                        });

                                                        updateCompanyAddonsHandler(newAddons);
                                                    }}
                                                />
                                                <span>{t(`addon.${addon}`)}</span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeTab === "horama" && (
                            <div className={styles.horamaManagement}>
                                <h3>{t("horamaIntegration")}</h3>
                                <p style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>
                                    {t("horamaDescription")}
                                </p>

                                {/* Current status */}
                                {horamaConfig?.configured && (
                                    <div
                                        style={{
                                            padding: "16px",
                                            backgroundColor: "#e8f5e9",
                                            borderRadius: "8px",
                                            marginBottom: "20px",
                                        }}
                                    >
                                        <h4 style={{ color: "#2e7d32", margin: "0 0 8px 0" }}>
                                            ✓ {t("horamaConfigured")}
                                        </h4>
                                        <p style={{ margin: "4px 0", fontSize: "14px" }}>
                                            <strong>{t("instanceUrl")}:</strong> {horamaConfig.instanceUrl}
                                        </p>
                                        {horamaConfig.zooSlug && (
                                            <p style={{ margin: "4px 0", fontSize: "14px" }}>
                                                <strong>{t("zooSlug")}:</strong> {horamaConfig.zooSlug}
                                            </p>
                                        )}
                                        {horamaConfig.speciesCount !== undefined && (
                                            <p style={{ margin: "4px 0", fontSize: "14px" }}>
                                                <strong>{t("speciesCount")}:</strong> {horamaConfig.speciesCount}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Error/Success messages */}
                                {horamaError && (
                                    <div
                                        style={{
                                            padding: "12px",
                                            backgroundColor: "#ffebee",
                                            color: "#c62828",
                                            borderRadius: "8px",
                                            marginBottom: "16px",
                                            fontSize: "14px",
                                        }}
                                    >
                                        {horamaError}
                                    </div>
                                )}
                                {horamaSuccess && (
                                    <div
                                        style={{
                                            padding: "12px",
                                            backgroundColor: "#e8f5e9",
                                            color: "#2e7d32",
                                            borderRadius: "8px",
                                            marginBottom: "16px",
                                            fontSize: "14px",
                                        }}
                                    >
                                        {horamaSuccess}
                                    </div>
                                )}

                                {/* Configuration form */}
                                <div style={{ marginBottom: "16px" }}>
                                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                                        {t("horamaInstanceUrl")} *
                                    </label>
                                    <TextInput
                                        placeholder="https://votre-zoo.horama.ai"
                                        value={horamaInstanceUrl}
                                        onChange={(e) => setHoramaInstanceUrl(e.target.value)}
                                        size="medium"
                                    />
                                    <p style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                                        {t("horamaInstanceUrlHelp")}
                                    </p>
                                </div>

                                <div style={{ marginBottom: "20px" }}>
                                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                                        {t("horamaApiKey")} *
                                    </label>
                                    <TextInput
                                        placeholder="prism_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                        value={horamaApiKey}
                                        onChange={(e) => setHoramaApiKey(e.target.value)}
                                        size="medium"
                                    />
                                    <p style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                                        {t("horamaApiKeyHelp")}
                                    </p>
                                </div>

                                <div style={{ display: "flex", gap: "12px" }}>
                                    <button
                                        className={styles.addBtn}
                                        onClick={handleSaveHoramaConfig}
                                        disabled={horamaLoading || !horamaInstanceUrl || !horamaApiKey}
                                        style={{
                                            padding: "10px 20px",
                                            opacity: horamaLoading || !horamaInstanceUrl || !horamaApiKey ? 0.5 : 1,
                                        }}
                                    >
                                        {horamaLoading ? t("saving") : t("saveAndTest")}
                                    </button>

                                    {horamaConfig?.configured && (
                                        <button
                                            className={styles.removeBtn}
                                            onClick={handleRemoveHoramaConfig}
                                            disabled={horamaLoading}
                                            style={{
                                                padding: "10px 20px",
                                                opacity: horamaLoading ? 0.5 : 1,
                                            }}
                                        >
                                            {t("removeConfiguration")}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles.footer}>
                        <button onClick={onClose}>{t("close")}</button>
                    </div>
                </div>
            </div>
        </ModalPortal>
    );
};

export default CompanyEditModal;
